import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import type { DateTime } from "luxon";
import { type MouseEvent, useCallback, useMemo, useState } from "react";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useSnackbar } from "@/contexts/SnackbarContext";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { useUpdateGeneralForm } from "@/pages/Settings/DepartmentConfiguration/forms/hooks";
import type { GeneralFormData } from "@/pages/Settings/DepartmentConfiguration/forms/types";
import { getFormDataFromDept } from "@/pages/Settings/DepartmentConfiguration/utils";
import { backendService } from "@/services/backend.service";
import type { LocationItem, RecurringShift, RecurringShiftRecurrenceType } from "@/utils/models";

export const useFetchMasterSchedule = ({
	startDate,
	disabled,
	recurringTypes,
}: {
	startDate: DateTime | undefined;
	disabled?: boolean;
	recurringTypes?: (keyof typeof RecurringShiftRecurrenceType)[];
}) => {
	const { chosenDepartment } = useChosenDepartment();
	const { trackError } = useAnalytics();
	const isRuntimeWeekStartEnabled = useFeatureFlag(FEATURE_FLAGS.ScheduleTemplateRuntimeWeekStart);

	return useQuery({
		queryKey: ["recurringMasterSchedule", chosenDepartment?.department_id, startDate?.toISO(), recurringTypes, isRuntimeWeekStartEnabled],
		queryFn: async (): Promise<RecurringShift[]> => {
			if (startDate) {
				const response = await backendService.getDepartmentRecurringShiftsForMasterSchedule(
					chosenDepartment?.department_id,
					startDate,
					recurringTypes,
					isRuntimeWeekStartEnabled,
				);
				return response.data.map(
					(shift) =>
						({
							...shift,
							startDate: typeof shift.startDate === "string" ? shift.startDate : (shift.startDate as DateTime).toFormat("yyyy-MM-dd"),

							endDate:
								shift.endDate != null ? (typeof shift.endDate === "string" ? shift.endDate : (shift.endDate as DateTime).toFormat("yyyy-MM-dd")) : undefined,
						}) as RecurringShift,
				);
			}
			return [];
		},
		throwOnError: (error) => {
			const axiosError = error instanceof AxiosError ? error : null;
			trackError("fetch_recurring_shifts_master_schedule_error", {
				details: axiosError?.message ?? error.message,
				stack: axiosError?.stack ?? error.stack,
				cause: JSON.stringify(axiosError?.cause ?? error.cause),
				status: axiosError?.status ?? "unknown",
			});
			return false;
		},
		enabled: !!chosenDepartment?.department_id && !disabled && !!startDate,
		refetchOnWindowFocus: false,
	});
};

const roleOrder = {
	Nurse: 1,
	CNA: 2,
	RNA: 3,
	CMA: 4,
} as const satisfies Record<string, number>;

export const getSectionSortOrder = (role: string): number => {
	if (role === "") return 0;

	for (const [key, order] of Object.entries(roleOrder)) {
		if (key && role.toLowerCase().includes(key.toLowerCase())) {
			return order;
		}
	}

	return 999;
};

/**
 * Whether this department keeps its own units and floors and enters census by them: no active EMR reports census.
 * The one statement of that rule; the census menu, the units/floors editor and the Locations page's census-area chip
 * all read it.
 */
export const useCanEditCensusByLocation = (): boolean => {
	const { departmentEmrType } = useHospitalData();
	return !departmentEmrType;
};

export const useEditLocations = () => {
	const { hospitalData, chosenDepartment, afterScheduleWrite, showErrorSnackbar } = useHospitalData();
	const { showSnackbar } = useSnackbar();

	const canEditCensusByLocation = useCanEditCensusByLocation();

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [units, setUnits] = useState<LocationItem[]>([]);
	const [floors, setFloors] = useState<LocationItem[]>([]);
	// The lists arrive with the department data, which pages don't wait for; submitting before then would save empty lists.
	const listsLoaded = hospitalData.units !== undefined && hospitalData.floors !== undefined;

	/** Opens the editor on the department's current lists, so it never edits a copy taken before they loaded or a cancelled draft. */
	const openEditor = useCallback(() => {
		if (hospitalData.units === undefined || hospitalData.floors === undefined) return;
		setUnits(hospitalData.units.map((unit) => ({ id: `unit-${unit}-${Date.now()}-${Math.random()}`, value: unit })));
		setFloors(hospitalData.floors.map((floor) => ({ id: `floor-${floor}-${Date.now()}-${Math.random()}`, value: floor })));
		setIsOpen(true);
	}, [hospitalData.units, hospitalData.floors]);

	const generalFormData = useMemo<GeneralFormData>(
		() => ({ ...getFormDataFromDept(chosenDepartment), units: units.map((u) => u.value), floors: floors.map((f) => f.value) }),
		[chosenDepartment, units, floors],
	);

	const { mutateAsync: updateGeneralForm, isPending } = useUpdateGeneralForm(generalFormData);

	const handleSubmit = async (e: MouseEvent): Promise<void> => {
		try {
			e.stopPropagation();
			await updateGeneralForm();
			await afterScheduleWrite({ allWeeks: true });
			showSnackbar("Units and floors updated successfully", "success");
			setIsOpen(false);
		} catch (error) {
			showErrorSnackbar("Failed to update units and floors", {}, error);
		}
	};

	return {
		isOpen,
		setIsOpen,
		openEditor,
		listsLoaded,
		units,
		setUnits,
		floors,
		setFloors,
		isUpdating: isPending,
		handleSubmit,
		canEditCensusByLocation,
	};
};
